import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import {
  IPaginatedResponse,
  IPagination,
  IProduct,
  IProductCategory,
  IProductFilters,
  IProductReviewsResponse,
  ProductStatus
} from '../../interfaces/product.interface';

/** Message d'erreur de l'API (`{error:{message}}`, ou chaîne brute sur les 500). */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    const body = error.error;
    if (typeof body === 'string' && body.trim()) return body;
    if (body?.error?.message) return body.error.message;
    if (body?.message) return body.message;
  }
  return fallback;
}

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  readonly #http: HttpClient = inject(HttpClient);
  readonly #toastrService: ToastrService = inject(ToastrService);
  readonly #translate: TranslateService = inject(TranslateService);
  readonly #apiBaseUrl: string = `${environment.apiUrl}/products`;

  #isLoading: WritableSignal<boolean> = signal(false);
  #isSaving: WritableSignal<boolean> = signal(false);
  #products: WritableSignal<IProduct[]> = signal<IProduct[]>([]);
  #pagination: WritableSignal<IPagination | null> = signal<IPagination | null>(null);
  #categories$: Observable<IProductCategory[]> | null = null;

  products: Signal<IProduct[]> = this.#products.asReadonly();
  pagination: Signal<IPagination | null> = this.#pagination.asReadonly();
  isLoading: Signal<boolean> = this.#isLoading.asReadonly();
  isSaving: Signal<boolean> = this.#isSaving.asReadonly();
  page: WritableSignal<number> = signal(1);
  filters: WritableSignal<IProductFilters> = signal<IProductFilters>({});

  readonly pageSize = 12;

  // ── Catalogue public ──────────────────────────────────────────────────────

  /** Liste paginée et filtrée côté serveur (produits actifs de vendeurs actifs). */
  getProducts(limit: number = this.pageSize): Observable<IPaginatedResponse<IProduct>> {
    this.#isLoading.set(true);
    const filters = this.filters();
    let params = new HttpParams().set('page', this.page()).set('limit', limit);
    if (filters.search?.trim()) params = params.set('search', filters.search.trim());
    if (filters.category_id) params = params.set('category_id', filters.category_id);
    if (filters.min_price) params = params.set('min_price', filters.min_price);
    if (filters.max_price) params = params.set('max_price', filters.max_price);

    return this.#http.get<IPaginatedResponse<IProduct>>(this.#apiBaseUrl, { params }).pipe(
      tap(res => {
        this.#products.set(res.data ?? []);
        this.#pagination.set(res.pagination ?? null);
      }),
      catchError(error => {
        this.#products.set([]);
        this.#pagination.set(null);
        return throwError(() => error);
      }),
      finalize(() => this.#isLoading.set(false))
    );
  }

  /** Catégories (mises en cache pour la session ; [] si l'API échoue). */
  getCategories(): Observable<IProductCategory[]> {
    if (!this.#categories$) {
      this.#categories$ = this.#http.get<{ data: IProductCategory[] }>(`${this.#apiBaseUrl}/categories`).pipe(
        map(res => res.data ?? []),
        catchError(() => {
          this.#categories$ = null;
          return of([]);
        }),
        shareReplay(1)
      );
    }
    return this.#categories$;
  }

  /** Détail d'un produit. Renvoie null si introuvable (404 ou produit inactif). */
  getProduct(productId: number): Observable<IProduct | null> {
    return this.#http.get<{ data: IProduct }>(`${this.#apiBaseUrl}/${productId}`).pipe(
      map(res => res.data ?? null),
      catchError(() => of(null))
    );
  }

  /** Produits actifs d'un vendeur (sans toucher à l'état du catalogue). */
  getSellerProducts(seller: { salon_id?: number | null; hairdresser_id?: number | null }, limit: number = 8): Observable<IProduct[]> {
    let params = new HttpParams().set('page', 1).set('limit', limit);
    if (seller.hairdresser_id) params = params.set('hairdresser_id', seller.hairdresser_id);
    else if (seller.salon_id) params = params.set('salon_id', seller.salon_id);
    else return of([]);
    return this.#http.get<IPaginatedResponse<IProduct>>(this.#apiBaseUrl, { params }).pipe(map(res => res.data ?? []));
  }

  // ── Avis ──────────────────────────────────────────────────────────────────

  getReviews(productId: number, page: number = 1, limit: number = 5): Observable<IProductReviewsResponse> {
    const params = new HttpParams().set('page', page).set('limit', limit);
    return this.#http.get<IProductReviewsResponse>(`${this.#apiBaseUrl}/${productId}/reviews`, { params });
  }

  /** Crée ou met à jour l'avis de la cliente connectée (upsert). */
  saveMyReview(productId: number, rating: number, comment: string): Observable<unknown> {
    return this.#http.put(`${this.#apiBaseUrl}/${productId}/reviews/me`, { rating, comment }).pipe(
      tap(() => this.#toastrService.success(this.#translate.instant('SHOP.REVIEWS.SAVED'))),
      catchError(error => {
        this.#toastrService.error(apiErrorMessage(error, this.#translate.instant('SHOP.ERRORS.GENERIC')));
        return throwError(() => error);
      })
    );
  }

  // ── Favoris (clientes) ────────────────────────────────────────────────────

  addFavorite(productId: number): Observable<unknown> {
    return this.#http.post(`${this.#apiBaseUrl}/${productId}/favorite`, {});
  }

  removeFavorite(productId: number): Observable<unknown> {
    return this.#http.delete(`${this.#apiBaseUrl}/${productId}/favorite`);
  }

  getMyFavorites(page: number = 1, limit: number = 50): Observable<IPaginatedResponse<IProduct>> {
    const params = new HttpParams().set('page', page).set('limit', limit);
    return this.#http.get<IPaginatedResponse<IProduct>>(`${this.#apiBaseUrl}/me/favorites`, { params });
  }

  // ── Espace vendeur (salon ou coiffeuse — le vendeur est déduit du token) ──

  getMyProducts(status?: ProductStatus | null, page: number = 1, limit: number = 50): Observable<IPaginatedResponse<IProduct>> {
    let params = new HttpParams().set('page', page).set('limit', limit);
    if (status) params = params.set('status', status);
    return this.#http.get<IPaginatedResponse<IProduct>>(`${this.#apiBaseUrl}/my-products`, { params });
  }

  createProduct(formData: FormData): Observable<unknown> {
    return this.#save(this.#http.post(this.#apiBaseUrl, formData), 'SHOP.SELLER.CREATED');
  }

  updateProduct(productId: number, formData: FormData): Observable<unknown> {
    return this.#save(this.#http.put(`${this.#apiBaseUrl}/${productId}`, formData), 'SHOP.SELLER.UPDATED');
  }

  archiveProduct(productId: number): Observable<unknown> {
    return this.#save(this.#http.patch(`${this.#apiBaseUrl}/${productId}/archive`, {}), 'SHOP.SELLER.ARCHIVED');
  }

  restoreProduct(productId: number): Observable<unknown> {
    return this.#save(this.#http.patch(`${this.#apiBaseUrl}/${productId}/restore`, {}), 'SHOP.SELLER.RESTORED');
  }

  deleteImage(productId: number, imageId: number): Observable<unknown> {
    return this.#save(this.#http.delete(`${this.#apiBaseUrl}/${productId}/images/${imageId}`));
  }

  setPrimaryImage(productId: number, imageId: number): Observable<unknown> {
    return this.#save(this.#http.patch(`${this.#apiBaseUrl}/${productId}/images/${imageId}/primary`, {}));
  }

  #save<T>(request: Observable<T>, successKey?: string): Observable<T> {
    this.#isSaving.set(true);
    return request.pipe(
      tap(() => {
        if (successKey) this.#toastrService.success(this.#translate.instant(successKey));
      }),
      catchError(error => {
        this.#toastrService.error(apiErrorMessage(error, this.#translate.instant('SHOP.ERRORS.GENERIC')));
        return throwError(() => error);
      }),
      finalize(() => this.#isSaving.set(false))
    );
  }
}
